/**
 * Customer group-discount 折扣卡 editor (S2.3) — add AND edit in one modal,
 * the UI seat for the already-shipped reason-gated `saveCustomerGroupDiscount`
 * store action (audit entity 'customer_group_discount').
 *
 * Shape rules (all pre-existing, this modal only surfaces them):
 * - kind is discount_pct XOR price_level per the CustomerGroupDiscount union —
 *   percent-only economics, NO amount-off variant exists (fence 6).
 * - One card per (customer, group) pair (Discribe-20 大潤發 shape): the store
 *   reducer upserts by the PAIR, so saving onto an occupied pair REPLACES the
 *   existing card — the modal says so instead of letting it surprise.
 * - A price_level card resolves as no-discount until its semantics are cited
 *   (typed TODO in groups.ts) — the modal states that, display-truth rule.
 *
 * Saving is price-affecting ⇒ the reason gate (ReasonRequiredModal) is the
 * only path, exactly like ScheduleEditorModal.
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, InputNumber, Modal, Radio, Select, Typography, theme } from 'antd';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import ReasonRequiredModal from '@/components/common/ReasonRequiredModal';
import { useCustomerOptions } from '@/hooks/useCustomers';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens } from '@/styles/tokens';
import type { CustomerGroupDiscount, PriceLevel, PricingGroup } from '@/types/api/pricing';

const { Text } = Typography;

const cardFormSchema = z
  .object({
    customerId: z.number().min(1, 'pricing.discountsAdmin.modal.validation.customerRequired'),
    groupId: z.string().min(1, 'pricing.discountsAdmin.modal.validation.groupRequired'),
    kind: z.enum(['discount_pct', 'price_level']),
    discountPct: z.number().nullable(),
    priceLevelId: z.string().nullable(),
  })
  .superRefine((values, ctx) => {
    if (values.kind === 'discount_pct') {
      if (values.discountPct === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['discountPct'],
          message: 'pricing.discountsAdmin.modal.validation.discountRequired',
        });
      } else if (values.discountPct < 0 || values.discountPct > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['discountPct'],
          message: 'pricing.discountsAdmin.modal.validation.discountRange',
        });
      }
    } else if (!values.priceLevelId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['priceLevelId'],
        message: 'pricing.discountsAdmin.modal.validation.levelRequired',
      });
    }
  });

type CardFormValues = z.infer<typeof cardFormSchema>;

const emptyValues: CardFormValues = {
  customerId: 0,
  groupId: '',
  kind: 'discount_pct',
  discountPct: null,
  priceLevelId: null,
};

export interface GroupDiscountCardModalProps {
  open: boolean;
  /** Absent ⇒ create mode. */
  card?: CustomerGroupDiscount | null;
  /** The pricing groups a card can reference (host passes the dataset rows). */
  groups: PricingGroup[];
  /** Active price levels (the price_level variant's target). */
  levels: PriceLevel[];
  /** Existing cards — the one-card-per-pair replace hint basis. */
  existingCards: CustomerGroupDiscount[];
  onCancel: () => void;
  /** Price-affecting ⇒ always carries the operator's reason. */
  onSave: (cardRow: CustomerGroupDiscount, reason: string) => void;
}

export const GroupDiscountCardModal = ({
  open,
  card,
  groups,
  levels,
  existingCards,
  onCancel,
  onSave,
}: GroupDiscountCardModalProps) => {
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const { customers } = useCustomerOptions();
  const [reasonOpen, setReasonOpen] = useState(false);
  const [pending, setPending] = useState<CardFormValues | null>(null);

  const isEdit = !!card;
  const zh = i18n.language.startsWith('zh');

  const values = useMemo<CardFormValues>(
    () =>
      card
        ? {
            customerId: card.customerId,
            groupId: card.groupId,
            kind: card.kind,
            discountPct: card.kind === 'discount_pct' ? card.discountPct : null,
            priceLevelId: card.kind === 'price_level' ? card.priceLevelId : null,
          }
        : emptyValues,
    [card]
  );

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<CardFormValues>({
    resolver: zodResolver(cardFormSchema),
    defaultValues: values,
    values,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    if (open) reset(values);
  }, [open, values, reset]);

  const watchedKind = useWatch({ control, name: 'kind' });
  const watchedCustomerId = useWatch({ control, name: 'customerId' });
  const watchedGroupId = useWatch({ control, name: 'groupId' });
  const watchedDiscountPct = useWatch({ control, name: 'discountPct' });

  /** One card per pair — saving onto an occupied pair replaces it (Discribe-20). */
  const replacesExisting = useMemo(
    () =>
      existingCards.some(
        (candidate) =>
          candidate.customerId === watchedCustomerId &&
          candidate.groupId === watchedGroupId &&
          candidate.id !== card?.id
      ),
    [existingCards, watchedCustomerId, watchedGroupId, card]
  );

  const submit = (formValues: CardFormValues) => {
    setPending(formValues);
    setReasonOpen(true);
  };

  const buildRow = (formValues: CardFormValues): CustomerGroupDiscount => {
    const customerName = customers.find(
      (candidate) => candidate.customerId === formValues.customerId
    )?.customerName;
    const base = {
      id: card?.id ?? `cgd-${formValues.customerId}-${formValues.groupId}-${Date.now()}`,
      customerId: formValues.customerId,
      ...(customerName ? { customerName } : {}),
      groupId: formValues.groupId,
    };
    return formValues.kind === 'discount_pct'
      ? { ...base, kind: 'discount_pct', discountPct: formValues.discountPct ?? 0 }
      : { ...base, kind: 'price_level', priceLevelId: formValues.priceLevelId ?? '' };
  };

  const fieldErrorText = (key?: string) =>
    key ? <Text style={formStyles.errorStyle}>{t(key)}</Text> : null;

  return (
    <>
      <Modal
        title={t(
          isEdit
            ? 'pricing:pricing.discountsAdmin.modal.editTitle'
            : 'pricing:pricing.discountsAdmin.modal.createTitle'
        )}
        open={open}
        onCancel={onCancel}
        onOk={handleSubmit(submit)}
        okText={isEdit ? t('common:actions.save') : t('common:actions.add')}
        cancelText={t('common:actions.cancel')}
        width={560}
        destroyOnHidden
        okButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
        cancelButtonProps={{ style: { height: `${controlHeightTokens.controlHeightXL}px` } }}
      >
        <div style={{ display: 'grid', gap: token.marginSM }}>
          <div>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('pricing:pricing.discountsAdmin.modal.customerLabel')}
            </Text>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || undefined}
                  onChange={(value) => {
                    field.onChange(value);
                    trigger('customerId');
                  }}
                  onBlur={field.onBlur}
                  size="large"
                  showSearch
                  optionFilterProp="label"
                  options={customers.map((customer) => ({
                    label: customer.customerName,
                    value: customer.customerId,
                  }))}
                  status={errors.customerId ? 'error' : undefined}
                  style={{ ...formStyles.inputStyle, width: '100%' }}
                  aria-label={t('pricing:pricing.discountsAdmin.modal.customerLabel')}
                />
              )}
            />
            {fieldErrorText(errors.customerId?.message)}
          </div>

          <div>
            <Text style={formStyles.labelStyle}>
              <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
              {t('pricing:pricing.discountsAdmin.modal.groupLabel')}
            </Text>
            <Controller
              name="groupId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || undefined}
                  onChange={(value) => {
                    field.onChange(value);
                    trigger('groupId');
                  }}
                  onBlur={field.onBlur}
                  size="large"
                  showSearch
                  optionFilterProp="label"
                  options={groups
                    .filter((group) => group.isActive || group.id === card?.groupId)
                    .map((group) => ({
                      label: `${group.code} · ${zh ? group.nameZh : group.nameEn}`,
                      value: group.id,
                    }))}
                  status={errors.groupId ? 'error' : undefined}
                  style={{ ...formStyles.inputStyle, width: '100%' }}
                  aria-label={t('pricing:pricing.discountsAdmin.modal.groupLabel')}
                />
              )}
            />
            {fieldErrorText(errors.groupId?.message)}
          </div>

          {replacesExisting && (
            <Alert
              type="info"
              showIcon
              message={t('pricing:pricing.discountsAdmin.modal.replaceHint')}
            />
          )}

          <div>
            <Text style={formStyles.labelStyle}>
              {t('pricing:pricing.discountsAdmin.modal.kindLabel')}
            </Text>
            <Controller
              name="kind"
              control={control}
              render={({ field }) => (
                <Radio.Group
                  value={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                    trigger('kind');
                  }}
                  options={[
                    {
                      label: t('pricing:pricing.discountsAdmin.kind.discountPct'),
                      value: 'discount_pct',
                    },
                    {
                      label: t('pricing:pricing.discountsAdmin.kind.priceLevel'),
                      value: 'price_level',
                    },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                  size="large"
                />
              )}
            />
          </div>

          {watchedKind === 'discount_pct' ? (
            <div>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.discountsAdmin.modal.discountLabel')}
              </Text>
              <Controller
                name="discountPct"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    value={field.value}
                    onChange={(value) => field.onChange(value ?? null)}
                    onBlur={field.onBlur}
                    size="large"
                    min={0}
                    max={100}
                    addonAfter="%"
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    status={errors.discountPct ? 'error' : undefined}
                    aria-label={t('pricing:pricing.discountsAdmin.modal.discountLabel')}
                  />
                )}
              />
              {fieldErrorText(errors.discountPct?.message)}
              {(watchedDiscountPct ?? 0) >= 100 && (
                <Alert
                  type="warning"
                  showIcon
                  message={t('pricing:pricing.warnings.discountZeroPrice')}
                  style={{ marginTop: token.marginXS }}
                />
              )}
            </div>
          ) : (
            <div>
              <Text style={formStyles.labelStyle}>
                <span style={{ color: token.colorError, marginRight: '4px' }}>*</span>
                {t('pricing:pricing.discountsAdmin.modal.levelLabel')}
              </Text>
              <Controller
                name="priceLevelId"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ?? undefined}
                    onChange={(value) => {
                      field.onChange(value);
                      trigger('priceLevelId');
                    }}
                    onBlur={field.onBlur}
                    size="large"
                    showSearch
                    optionFilterProp="label"
                    options={levels.map((level) => ({
                      label: `${level.code} · ${zh ? level.nameZh : level.nameEn}`,
                      value: level.id,
                    }))}
                    status={errors.priceLevelId ? 'error' : undefined}
                    style={{ ...formStyles.inputStyle, width: '100%' }}
                    aria-label={t('pricing:pricing.discountsAdmin.modal.levelLabel')}
                  />
                )}
              />
              {fieldErrorText(errors.priceLevelId?.message)}
              {/* Display truth: the level variant ships as a row shape only —
                  the resolver treats it as no-discount until its semantics are
                  cited (typed TODO in groups.ts). */}
              <Text
                type="secondary"
                style={{ display: 'block', marginTop: 4, fontSize: token.fontSizeSM }}
              >
                {t('pricing:pricing.discountsAdmin.modal.levelSemanticsHint')}
              </Text>
            </div>
          )}
        </div>
      </Modal>

      <ReasonRequiredModal
        open={reasonOpen}
        title={t('pricing:pricing.discountsAdmin.reason.title')}
        description={t('pricing:pricing.discountsAdmin.reason.description')}
        reasonLabel={t('pricing:pricing.discountsAdmin.reason.label')}
        reasonPlaceholder={t('pricing:pricing.discountsAdmin.reason.placeholder')}
        requiredMessage={t('pricing:pricing.discountsAdmin.reason.required')}
        submitText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        onSubmit={(reason) => {
          setReasonOpen(false);
          if (pending) onSave(buildRow(pending), reason);
          setPending(null);
        }}
        onCancel={() => setReasonOpen(false)}
      />
    </>
  );
};

export default GroupDiscountCardModal;
